import { AnyAction } from 'redux';
import { STATE } from 'app/constants';
import { ApplicationState } from 'app/types-store';
import { ActionDispatching } from 'app/types-shared';
import { complexValidationFromServer } from 'app/infrastructure/data/validation';
import { ErrorNotification, SuccessNotification } from 'app/infrastructure/notifications/user-messages';
import * as navigate from 'app/area/shared/navigation';
import { isForbiddenStatus, blockAccess, isNotFoundStatus } from 'app/area/shared/actions-utils';
import { mapExperienceTransferFromWire, mapExperienceTransferToWire } from './utils/map-experience-transfer';
import { experienceTransferTranslation } from '../constants';
import {
  RequestExperienceTransfer, ReceiveExperienceTransfer, FailExperienceTransfer,
  RequestDeleteExperienceTransfer, ReceiveDeleteExperienceTransfer, FailDeleteExperienceTransfer,
  RequestSaveExperienceTransfer, ReceiveSaveExperienceTransfer, FailSaveExperienceTransfer,
  UpdateItem, ClearForm, UpdateValidation, ResetValidation,
  ClearValidation
} from './action-types';
import { api } from './api';
import { getBackRoute } from './route';
import {
  ExperienceTransferItem, ExperienceTransferItemValidation
} from './types-store';
import { ExperienceTransferActionType } from 'app/area/experience-transfer/form/types';
import { replaceTemplateString } from 'app/infrastructure/platform';
import { hasKeyViolationValidation } from 'app/area/experience-transfer/form/utils/validation';

export const actionCreators = {

  createExperienceTransfer: (): ActionDispatching<AnyAction, ApplicationState> =>
    (dispatch, getState) => dispatch(new ReceiveExperienceTransfer(mapExperienceTransferFromWire({ active: true }))),

  loadExperienceTransfer: (id: number): ActionDispatching<AnyAction, ApplicationState> =>
    async (dispatch, getState) => {

      dispatch(new RequestExperienceTransfer());

      try {

        const experienceTransferWired = await api.loadExperienceTransfer(id);
        const experienceTransferMapped = mapExperienceTransferFromWire(experienceTransferWired);

        dispatch(new ReceiveExperienceTransfer(experienceTransferMapped));

      } catch (err) {

        if (isForbiddenStatus(err)) {
          blockAccess({
            dispatch,
            getState,
            area: STATE.experienceTransfer,
            translationId: experienceTransferTranslation.no_access_to_experience_transfer
          });
        } else {

          const errorMessage = err && (err.userMessages || err.message);
          !!errorMessage && dispatch(new ErrorNotification(errorMessage));

          navigate.to({ pathname: getBackRoute() });
        }

        dispatch(new FailExperienceTransfer());
      }
    },

  deleteExperienceTransfer: (id: number, text: string, successMessage: string): ActionDispatching<AnyAction, ApplicationState> =>
    async (dispatch, getState) => {

      dispatch(new RequestDeleteExperienceTransfer());

      try {
        await api.deleteExperienceTransfer(id);

        navigate.to({ pathname: getBackRoute() }, { state: { internal: true } });

        dispatch(new ReceiveDeleteExperienceTransfer());
        dispatch(new SuccessNotification(replaceTemplateString(successMessage, text)));
      } catch (err) {
        const error = err && (err.userMessages || err.message);
        error && dispatch(new ErrorNotification(error));
        dispatch(new FailDeleteExperienceTransfer());

        if (isNotFoundStatus(err)) {
          navigate.to({ pathname: getBackRoute() }, { state: { internal: true } });
        }
      }
    },

  saveExperienceTransfer: (
    actionType: ExperienceTransferActionType,
    experienceTransferItem: ExperienceTransferItem,
    successMessage: string,
    validationErrorMessage: string
  ): ActionDispatching<AnyAction, ApplicationState> =>
    async (dispatch, getState) => {

      const id = experienceTransferItem?.id;

      dispatch(new RequestSaveExperienceTransfer());

      try {
        const experienceTransferWire = mapExperienceTransferToWire(experienceTransferItem);

        if (actionType === ExperienceTransferActionType.edit) {
          await api.updateExperienceTransfer(id, experienceTransferWire);
        } else {
          await api.addExperienceTransfer(experienceTransferWire);
        }

        dispatch(new ReceiveSaveExperienceTransfer());

        navigate.to({ pathname: getBackRoute() }, { state: { internal: true } });
        dispatch(new SuccessNotification(successMessage));

      } catch (err) {
        if (Object.keys(err?.validationErrors ?? {}).length > 0) {
          dispatch(new ErrorNotification(validationErrorMessage));
          dispatch(new UpdateValidation(complexValidationFromServer(err.validationErrors)));
        }

        if (!hasKeyViolationValidation(err?.validationErrors)
          && (err?.userMessages?.length ?? 0 > 0)) {
          dispatch(new ErrorNotification(err.userMessages));
        }

        dispatch(new FailSaveExperienceTransfer());

        if (isNotFoundStatus(err)) {
          navigate.to({ pathname: getBackRoute() }, { state: { internal: true } });
        }
      }
    },

  resetValidation: (id: number, key?: string): ActionDispatching<AnyAction, ApplicationState> =>
    (dispatch: any, getState: any) => {
      dispatch(new ResetValidation(id, key));
    },

  updateItem: (item: Partial<ExperienceTransferItem>): ActionDispatching<AnyAction, ApplicationState> =>
    (dispatch, getState) => {
      dispatch(new UpdateItem(item));
    },

  updateValidation: (validation: ExperienceTransferItemValidation): ActionDispatching<AnyAction, ApplicationState> =>
    (dispatch, getState) => {
      dispatch(new UpdateValidation(validation));
    },

  clearValidation: () => (dispatch: any, getState: any) => {
    dispatch(new ClearValidation());
  },

  clearForm: (): ActionDispatching<AnyAction, ApplicationState> =>
    (dispatch, getState) => {
      dispatch(new ClearForm());
    },

  displayError: (errorMessage: string): ActionDispatching<AnyAction, ApplicationState> =>
    (dispatch, getState) => {
      dispatch(new ErrorNotification(errorMessage));
    }
};
